import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';

interface EnhancedChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const EnhancedChatInput = forwardRef<HTMLTextAreaElement, EnhancedChatInputProps>(
  ({ value, onChange, onSend, isLoading = false, placeholder = "Ask ThinkMate anything...", disabled = false, className = "" }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const inputRef = ref || textareaRef;

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        if (!isLoading && value.trim()) {
          onSend();
        }
      },
      [isLoading, value, onSend]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSubmit(e);
        }
      },
      [handleSubmit]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
      },
      [onChange]
    );

    // Focus input on mount
    useEffect(() => {
      if (inputRef && typeof inputRef === 'object' && inputRef.current) {
        inputRef.current.focus();
      }
    }, [inputRef]);

    return (
      <form onSubmit={handleSubmit} className={`w-full ${className}`}>
        <div className={`${ENHANCED_CHAT_INPUT_CONTAINER_CLASS}`}>
          <div className="flex items-end gap-2 p-3">
            {/* Auto-resizing textarea */}
            <TextareaAutosize
              ref={inputRef}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled || isLoading}
              className="flex-1 min-h-[20px] max-h-[120px] resize-none bg-transparent border-none outline-none text-gray-800 placeholder-gray-500 text-base leading-relaxed"
              minRows={1}
              maxRows={5}
            />
            {/* Send button */}
            <button
              type="submit"
              disabled={!value.trim() || isLoading || disabled}
              className="flex-shrink-0 p-2 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-all duration-200"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>
        </div>
      </form>
    );
  }
);

EnhancedChatInput.displayName = 'EnhancedChatInput';

export const ENHANCED_CHAT_INPUT_CONTAINER_CLASS = "relative bg-white border border-gray-200 rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-rose-500 focus-within:border-rose-500 transition-all duration-200";

export default EnhancedChatInput; 